import * as THREE from 'three';
import type { Vec3 } from '@shared/types';

// ============================================================
// Snapshot buffer + interpolation for smooth remote rendering
// ============================================================

interface Snapshot {
  readonly position: Vec3;
  readonly rotation: Vec3;
  readonly velocity: Vec3;
  readonly timestamp: number;
}

const BUFFER_SIZE = 10;
const INTERPOLATION_DELAY_MS = 150; // ~4-5 ticks at 30Hz

export class Interpolator {
  private readonly buffer: Snapshot[] = [];

  // Reusable objects to avoid allocation
  private readonly resultPos = new THREE.Vector3();
  private readonly resultQuat = new THREE.Quaternion();
  private readonly quatA = new THREE.Quaternion();
  private readonly quatB = new THREE.Quaternion();
  private readonly eulerA = new THREE.Euler();
  private readonly eulerB = new THREE.Euler();

  pushSnapshot(position: Vec3, rotation: Vec3, timestamp: number, velocity: Vec3 = { x: 0, y: 0, z: 0 }): void {
    this.buffer.push({ position, rotation, velocity, timestamp });

    // Keep buffer bounded
    if (this.buffer.length > BUFFER_SIZE) {
      this.buffer.shift();
    }
  }

  /**
   * Returns interpolated position and rotation for the given render time.
   * Render time is offset by INTERPOLATION_DELAY_MS behind real time.
   */
  getInterpolated(
    now: number,
    outPosition: THREE.Vector3,
    outEuler: THREE.Euler,
  ): boolean {
    if (this.buffer.length === 0) return false;

    const renderTime = now - INTERPOLATION_DELAY_MS;

    // Find the two snapshots bracketing renderTime
    let before: Snapshot | null = null;
    let after: Snapshot | null = null;

    for (let i = 0; i < this.buffer.length; i++) {
      if (this.buffer[i].timestamp <= renderTime) {
        before = this.buffer[i];
      } else {
        after = this.buffer[i];
        break;
      }
    }

    // Only have one side — use latest available
    if (!before && after) {
      outPosition.set(after.position.x, after.position.y, after.position.z);
      outEuler.set(after.rotation.x, after.rotation.y, after.rotation.z);
      return true;
    }

    if (before && !after) {
      // Extrapolate using velocity, capped at 500ms
      const elapsed = Math.min((renderTime - before.timestamp) / 1000, 0.5);
      outPosition.set(
        before.position.x + before.velocity.x * elapsed,
        before.position.y + before.velocity.y * elapsed,
        before.position.z + before.velocity.z * elapsed,
      );
      outEuler.set(before.rotation.x, before.rotation.y, before.rotation.z);
      return true;
    }

    if (!before || !after) return false;

    // Interpolate between the two snapshots
    const span = after.timestamp - before.timestamp;
    const t = span > 0 ? (renderTime - before.timestamp) / span : 0;
    const clamped = Math.max(0, Math.min(1, t));

    // Position: cubic hermite interpolation using velocity at both endpoints
    const dt = span / 1000; // span in seconds for velocity scaling
    const t2 = clamped * clamped;
    const t3 = t2 * clamped;
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + clamped;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    outPosition.set(
      h00 * before.position.x + h10 * before.velocity.x * dt + h01 * after.position.x + h11 * after.velocity.x * dt,
      h00 * before.position.y + h10 * before.velocity.y * dt + h01 * after.position.y + h11 * after.velocity.y * dt,
      h00 * before.position.z + h10 * before.velocity.z * dt + h01 * after.position.z + h11 * after.velocity.z * dt,
    );

    // Rotation: slerp via quaternions for smooth results
    this.eulerA.set(before.rotation.x, before.rotation.y, before.rotation.z);
    this.eulerB.set(after.rotation.x, after.rotation.y, after.rotation.z);
    this.quatA.setFromEuler(this.eulerA);
    this.quatB.setFromEuler(this.eulerB);
    this.resultQuat.slerpQuaternions(this.quatA, this.quatB, clamped);
    outEuler.setFromQuaternion(this.resultQuat);

    return true;
  }

  clear(): void {
    this.buffer.length = 0;
  }
}
